"use client";

import { Check, Palette, BookOpen, Clapperboard } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function StrategicCapabilities() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const tiers = [{
    number: "01",
    Icon: Palette,
    name: "Creative Direction",
    descriptor: "Defining the visual language of your brand's future.",
    benefits: ["Concept mood board development", "Visual identity systems", "Campaign art direction"],
    featured: false
  }, {
    number: "02",
    Icon: BookOpen,
    name: "Brand Storytelling",
    descriptor: "Crafting narratives that resonate across every channel.",
    benefits: ["Script treatment & copywriting", "Brand voice development", "Multi-platform content strategy"],
    featured: true
  }, {
    number: "03",
    Icon: Clapperboard,
    name: "Production Scaling",
    descriptor: "High-end execution from pre-production to final cut.",
    benefits: ["Full-scale video production", "Social-first content assets", "Post-production & color grading"],
    featured: false
  }];
  return <section data-section-id="549" id="strategic-capabilities" className="bg-background text-foreground py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        {}
        <div ref={header.ref} className={`mx-auto max-w-3xl text-center transition-all duration-700 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-mono text-xs tracking-[0.35em] uppercase" style={{
          color: "var(--primary)"
        }}>
            Our Expertise
          </p>
          <h2 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mt-6 leading-[1.05]">
            <em className="italic font-light" style={{
            color: "var(--primary)"
          }}>
              Strategic
            </em>{" "}
            Capabilities
          </h2>
          <p className="font-default text-base md:text-lg text-muted-foreground mt-6 max-w-xl mx-auto">
            Precision-engineered creative solutions designed to amplify your brand's market presence.
          </p>
          <div className="mx-auto mt-10 h-px w-16" style={{
          backgroundColor: "var(--primary)"
        }} />
        </div>

        {}
        <div className="mt-20 grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-0 relative">
          {tiers.map((tier, i) => {
          const Icon = tier.Icon;
          return <div key={tier.number} data-index={i} className={`group relative flex flex-col items-center text-center px-6 md:px-8 py-10 md:py-14 transition-all duration-500 ease-out hover:-translate-y-1 shadow-md ${tier.featured ? "md:-my-6 md:py-20 bg-accent md:scale-[1.03] z-10" : ""} ${i > 0 ? "md:border-l md:border-border" : ""}`}>
                {tier.featured && <p className="font-mono text-[10px] tracking-[0.35em] uppercase mb-4" style={{
              color: "var(--primary)"
            }}>
                    Core Focus
                  </p>}

                {}
                <div className={`font-default font-bold leading-none ${tier.featured ? "text-7xl md:text-8xl" : "text-6xl md:text-7xl"}`} style={{
              color: "var(--primary)"
            }}>
                  {tier.number}
                </div>

                {}
                <div className="mt-8 transition-transform duration-500 ease-out group-hover:rotate-[5deg]">
                  <Icon className="w-12 h-12 stroke-[1]" style={{
                color: "var(--primary)"
              }} />
                </div>

                {}
                <h3 className="font-default font-bold text-2xl md:text-[28px] text-foreground mt-8 leading-tight">
                  {tier.name}
                </h3>

                {}
                <p className="font-default text-base md:text-lg text-muted-foreground mt-3 max-w-xs">
                  {tier.descriptor}
                </p>

                {}
                <div className="mt-8 h-px w-12" style={{
              backgroundColor: "var(--primary)"
            }} />

                {}
                <ul className="mt-8 space-y-3 text-left">
                  {tier.benefits.map((benefit, bi) => <li key={bi} data-index={bi} className="flex items-start gap-3 font-default text-sm text-foreground/85">
                      <Check className="w-4 h-4 mt-0.5 stroke-[1.25] shrink-0" style={{
                  color: "var(--primary)"
                }} />
                      <span>{benefit}</span>
                    </li>)}
                </ul>

                {}
                <div className="mt-10">
                  <Button asChild variant="link" className="font-default font-semibold text-base p-0 h-auto hover:no-underline" style={{
                color: "var(--primary)"
              }}>
                    <Link to="/ExpertisePage">
                      <span className="relative">
                        Learn More{" "}
                        <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">
                          &rarr;
                        </span>
                      </span>
                    </Link>
                  </Button>
                </div>
              </div>;
        })}
        </div>
      </div>
    </section>;
}